// Pure enums/helpers — RSC-safe (no styled-components/react imports).
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
export enum RingType {
  ENGRAVED = 'ENGRAVED',
  NOT_ENGRAVED = 'NOT_ENGRAVED',
}

export const getEngravingKey = (ip: boolean | null | string) => {
  return ip ? RingType.ENGRAVED : RingType.NOT_ENGRAVED;
};

export enum AirRingColors {
  BIONIC_GOLD = 'BIONIC_GOLD',
  RAW_TITANIUM = 'RAW_TITANIUM',
  ASTER_BLACK = 'ASTER_BLACK',
  MATTE_GREY = 'MATTE_GREY',
  SPACE_SILVER = 'SPACE_SILVER',
  ROSE_GOLD = 'ROSE_GOLD',
}

export enum RingSizes {
  FIVE = 5,
  SIX = 6,
  SEVEN = 7,
  EIGHT = 8,
  NINE = 9,
  TEN = 10,
  ELEVEN = 11,
  TWELVE = 12,
  THIRTEEN = 13,
  FOURTEEN = 14,
  OPEN = 99,
}

import { BiMapWithDefaults } from 'utils/BiMapWithDefaults';

export type DisabledRingAirSkuRule = {
  /** Omit = all colors */
  colors?: readonly AirRingColors[];
  /** Omit = all sizes */
  sizes?: readonly RingSizes[];
  /** Omit = both ENGRAVED and NOT_ENGRAVED */
  types?: readonly RingType[];
  /** Omit = every ShopifyStore */
  stores?: readonly ShopifyStore[];
};

export type RingAirSkuQuery = {
  color?: AirRingColors;
  size?: RingSizes;
  type?: RingType;
  store?: ShopifyStore | null;
};

/**
 * Disable SKUs by color / size / engraving / store. Omitted fields are wildcards.
 * Remove a rule (or empty the list) to re-enable.
 */
export const DISABLED_RING_AIR_SKUS: readonly DisabledRingAirSkuRule[] = [
  { sizes: [RingSizes.FIVE] },
  { colors: [AirRingColors.ROSE_GOLD] },
];

const AIR_RING_COLOR_VALUES = Object.values(AirRingColors) as AirRingColors[];
const RING_SIZE_VALUES = Object.values(RingSizes).filter(
  (value): value is RingSizes => typeof value === 'number',
);
const RING_TYPE_VALUES = Object.values(RingType) as RingType[];

export function ringAirSkuRuleMatches(
  rule: DisabledRingAirSkuRule,
  query: RingAirSkuQuery,
): boolean {
  if (rule.colors) {
    if (query.color == null || !rule.colors.includes(query.color)) return false;
  }
  if (rule.sizes) {
    if (query.size == null || !rule.sizes.includes(query.size)) return false;
  }
  if (rule.types) {
    if (query.type == null || !rule.types.includes(query.type)) return false;
  }
  if (rule.stores) {
    if (query.store == null || !rule.stores.includes(query.store)) return false;
  }
  return true;
}

export function isRingAirSkuDisabled(
  query: RingAirSkuQuery,
  rules: readonly DisabledRingAirSkuRule[] = DISABLED_RING_AIR_SKUS,
): boolean {
  return rules.some((rule) => ringAirSkuRuleMatches(rule, query));
}

export function isRingAirColorDisabled(
  color: AirRingColors,
  store?: ShopifyStore | null,
  rules: readonly DisabledRingAirSkuRule[] = DISABLED_RING_AIR_SKUS,
): boolean {
  return RING_SIZE_VALUES.every((size) =>
    RING_TYPE_VALUES.every((type) =>
      isRingAirSkuDisabled({ color, size, type, store }, rules),
    ),
  );
}

export function getAvailableAirRingColors(
  store?: ShopifyStore | null,
  rules: readonly DisabledRingAirSkuRule[] = DISABLED_RING_AIR_SKUS,
): AirRingColors[] {
  return AIR_RING_COLOR_VALUES.filter(
    (color) => !isRingAirColorDisabled(color, store, rules),
  );
}

const FrontendBackendRingNameConst = {
  [AirRingColors.ASTER_BLACK]: 'air_aster_black',
  [AirRingColors.BIONIC_GOLD]: 'air_gold',
  [AirRingColors.SPACE_SILVER]: 'air_silver',
  [AirRingColors.MATTE_GREY]: 'matte_grey',
  [AirRingColors.RAW_TITANIUM]: 'raw_titanium',
  [AirRingColors.ROSE_GOLD]: 'brushed_rose_gold',
};

const WSFrontendBackendRingNameConst = {
  [AirRingColors.ASTER_BLACK]: 'ws_air_aster_black',
  [AirRingColors.BIONIC_GOLD]: 'ws_air_gold',
  [AirRingColors.SPACE_SILVER]: 'ws_air_silver',
  [AirRingColors.MATTE_GREY]: 'ws_matte_grey',
  [AirRingColors.RAW_TITANIUM]: 'ws_raw_titanium',
  [AirRingColors.ROSE_GOLD]: 'ws_brushed_rose_gold',
};

export const FrontendBackendRingNameMap = new BiMapWithDefaults(
  FrontendBackendRingNameConst,
);

/** Enum value or backend slug (`raw_titanium`). Unknown slugs stay unchosen. */
export function resolveAirRingColorParam(
  colorParam: string | null | undefined,
  available: readonly AirRingColors[],
): AirRingColors | null {
  if (!colorParam) return null;
  if ((available as readonly string[]).includes(colorParam)) {
    return colorParam as AirRingColors;
  }
  try {
    const mapped = FrontendBackendRingNameMap.getKey(
      colorParam,
    ) as AirRingColors;
    if ((available as readonly string[]).includes(mapped)) return mapped;
  } catch {
    return null;
  }
  return null;
}

export const WSFrontendBackendRingNameMap = new BiMapWithDefaults(
  FrontendBackendRingNameConst,
);

export const FrontendBackendRingNameMapWithDefault = new BiMapWithDefaults(
  FrontendBackendRingNameConst,
  AirRingColors.ASTER_BLACK, // default key
  'air_aster_black', // default value
);

export const WSFrontendBackendRingNameMapWithDefault = new BiMapWithDefaults(
  WSFrontendBackendRingNameConst,
  AirRingColors.ASTER_BLACK, // default key
  'ws_air_aster_black', // default value
);

export type RingVariantType = {
  variantId: Partial<Record<ShopifyStore, string>>;
  checkoutUrl: Partial<Record<ShopifyStore, string>>;
  fastDelivery?: boolean;
  SKU?: string;
  misc?: Record<MiscRingCheckoutTypes, any>;
};

export const RingPriceData: Record<
  ShopifyStore,
  Record<AirRingColors, { striked_price: number; price: number }>
> = {
  [ShopifyStore.IN]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 28499, price: 28499 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 28499, price: 28499 },
    [AirRingColors.MATTE_GREY]: { striked_price: 28499, price: 28499 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 28499, price: 28499 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 28499, price: 28499 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 33999, price: 33999 },
  },
  [ShopifyStore.ROW]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 349, price: 349 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 349, price: 349 },
    [AirRingColors.MATTE_GREY]: { striked_price: 349, price: 349 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 349, price: 349 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 349, price: 349 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 399, price: 399 },
  },
  [ShopifyStore.US]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 349, price: 349 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 349, price: 349 },
    [AirRingColors.MATTE_GREY]: { striked_price: 349, price: 349 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 349, price: 349 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 349, price: 349 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 399, price: 399 },
  },
  [ShopifyStore.AE]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 1299, price: 1299 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 1299, price: 1299 },
    [AirRingColors.MATTE_GREY]: { striked_price: 1299, price: 1299 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 1299, price: 1299 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 1299, price: 1299 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 1489, price: 1489 },
  },
  [ShopifyStore.GB]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 329, price: 329 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 329, price: 329 },
    [AirRingColors.MATTE_GREY]: { striked_price: 329, price: 329 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 329, price: 329 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 329, price: 329 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 379, price: 379 },
  },
  [ShopifyStore.EU]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 379, price: 379 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 379, price: 379 },
    [AirRingColors.MATTE_GREY]: { striked_price: 379, price: 379 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 379, price: 379 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 379, price: 379 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 434, price: 434 },
  },
  [ShopifyStore.AU]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 599, price: 599 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 599, price: 599 },
    [AirRingColors.MATTE_GREY]: { striked_price: 599, price: 599 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 599, price: 599 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 599, price: 599 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 699, price: 699 },
  },
  [ShopifyStore.CA]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 479, price: 479 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 479, price: 479 },
    [AirRingColors.MATTE_GREY]: { striked_price: 479, price: 479 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 479, price: 479 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 479, price: 479 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 549, price: 549 },
  },
  [ShopifyStore.SA]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 1509, price: 1509 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 1509, price: 1509 },
    [AirRingColors.MATTE_GREY]: { striked_price: 1509, price: 1509 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 1509, price: 1509 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 1509, price: 1509 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 1729, price: 1729 },
  },
  [ShopifyStore.MX]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 6899, price: 6899 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 6899, price: 6899 },
    [AirRingColors.MATTE_GREY]: { striked_price: 6899, price: 6899 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 6899, price: 6899 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 6899, price: 6899 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 8299, price: 8299 },
  },
  [ShopifyStore.ZA]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 7999, price: 7999 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 7999, price: 7999 },
    [AirRingColors.MATTE_GREY]: { striked_price: 7999, price: 7999 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 7999, price: 7999 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 7999, price: 7999 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 8999, price: 8999 },
  },
  [ShopifyStore.JP]: {
    [AirRingColors.RAW_TITANIUM]: { striked_price: 59800, price: 59800 },
    [AirRingColors.ASTER_BLACK]: { striked_price: 59800, price: 59800 },
    [AirRingColors.MATTE_GREY]: { striked_price: 59800, price: 59800 },
    [AirRingColors.BIONIC_GOLD]: { striked_price: 59800, price: 59800 },
    [AirRingColors.SPACE_SILVER]: { striked_price: 59800, price: 59800 },
    [AirRingColors.ROSE_GOLD]: { striked_price: 68800, price: 68800 },
  },
};


export enum MiscRingCheckoutTypes {
  BETTER_HELP_CHECKOUT = 'BETTER_HELP_CHECKOUT',
}
